import { fromMicro, sumDecimal, toMicro } from './decimal';
import type { BookingLine } from '../types/VendorBookingRecord';

const MICRO = BigInt(1000000);

/** quantity x unit price, exact, to two decimals (what the API stores as the line total); null when either is not a number. */
export function lineTotal(quantity: string, unitPrice: string): string | null {
  const q = toMicro(quantity);
  const p = toMicro(unitPrice);
  if (q === null || p === null) return null;
  return fromMicro((q * p) / MICRO);
}

/** One editable line while the page is open. `id` is the saved line's id, absent for a line not saved yet. */
export interface DraftLine {
  key: string;
  id?: string;
  description: string;
  quantity: string;
  unit_price: string;
}

export const draftFromLine = (l: BookingLine): DraftLine => ({
  key: l.id, id: l.id, description: l.description, quantity: l.quantity, unit_price: l.unit_price ?? '',
});

export const draftTotal = (lines: readonly DraftLine[]): string =>
  sumDecimal(lines.map(l => lineTotal(l.quantity, l.unit_price)));

const NUMBER = /^\d{1,8}(\.\d{1,4})?$/;
const PRICE = /^-?\d{1,10}(\.\d{1,2})?$/;

/** One plain sentence per line that cannot be saved, by line key. */
export function lineErrors(lines: readonly DraftLine[], canViewFinancials: boolean): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of lines) {
    if (!l.description.trim()) out[l.key] = 'Give the line a description.';
    else if (canViewFinancials && !NUMBER.test(l.quantity.trim())) out[l.key] = 'Quantity must be a number such as 2 or 1.5.';
    else if (canViewFinancials && !PRICE.test(l.unit_price.trim())) out[l.key] = 'Unit price must be an amount such as 1250 or 1250.50.';
  }
  return out;
}

export interface LinePlan {
  create: DraftLine[];
  update: DraftLine[];
  remove: BookingLine[];
}

/** What to send so the saved lines become the drafts: new lines created, changed ones updated, dropped ones removed. */
export function planLineSave(saved: readonly BookingLine[], drafts: readonly DraftLine[]): LinePlan {
  const byId = new Map(saved.map(l => [l.id, l]));
  const update = drafts.filter(d => {
    const s = d.id ? byId.get(d.id) : undefined;
    if (!s) return false;
    return s.description !== d.description.trim()
      || toMicro(s.quantity) !== toMicro(d.quantity)
      || toMicro(s.unit_price) !== toMicro(d.unit_price);
  });
  const kept = new Set(drafts.map(d => d.id).filter(Boolean));
  return {
    create: drafts.filter(d => !d.id),
    update,
    remove: saved.filter(s => !kept.has(s.id)),
  };
}
